import { useCallback, useEffect, useRef } from 'react'
import { ChatInputElementId } from '@renderer/chat/components'
import type { SidepanelState } from '@renderer/overlay/atoms'

/** Long enough for the open animation to settle before anything takes focus. */
const FOCUS_AFTER_OPEN_DELAY = 300

export type SidepanelFocusIntent = 'chat' | 'reminders' | null

interface SidepanelFocusIntentOptions {
  sidepanelState: SidepanelState
  focusIntent: SidepanelFocusIntent
  setFocusIntent: (intent: SidepanelFocusIntent) => void
  /** True while a View is open — false means Home is on top. */
  canGoBack: boolean
  /** Id of the mounted View, so Reminders can prefer its own input. */
  topViewId: string
}

/**
 * Who gets focus when the panel opens, and who is kept from taking it.
 *
 * The panel opens for three different reasons and each wants a different
 * answer: the Mini and the reminders hotkey open it *at* an input, while an
 * edge or hotkey open should land on nothing at all — the chat input otherwise
 * grabs focus during the transition and swallows the first keystroke.
 */
export function useSidepanelFocusIntent({
  sidepanelState,
  focusIntent,
  setFocusIntent,
  canGoBack,
  topViewId,
}: SidepanelFocusIntentOptions) {
  const prevSidepanelState = useRef(sidepanelState)

  // When the sidepanel opens (or a View pops back to Home), handle focus intent
  useEffect(() => {
    const justOpened = prevSidepanelState.current !== 'open' && sidepanelState === 'open'
    prevSidepanelState.current = sidepanelState

    if (justOpened) {
      if (focusIntent === 'chat') {
        // Mini open: focus chat input instead of preventing focus / focusing cells
        const timeoutId = setTimeout(() => {
          setFocusIntent(null)
          document.getElementById(ChatInputElementId)?.focus()
        }, FOCUS_AFTER_OPEN_DELAY)
        return () => {
          clearTimeout(timeoutId)
          setFocusIntent(null)
        }
      }

      // Immediately blur chat input if it got focused
      const chatInput = document.getElementById(ChatInputElementId)
      if (chatInput && chatInput.contains(document.activeElement)) {
        ;(document.activeElement as HTMLElement)?.blur()
      }

      // Intercept any focus attempts on the chat input during the open transition
      const preventChatFocus = (e: FocusEvent) => {
        if (chatInput && chatInput.contains(e.target as Node)) {
          ;(e.target as HTMLElement)?.blur()
        }
      }
      document.addEventListener('focusin', preventChatFocus)

      // After render settles, clean up the focusin listener
      const timeoutId = setTimeout(() => {
        document.removeEventListener('focusin', preventChatFocus)
      }, FOCUS_AFTER_OPEN_DELAY)
      return () => {
        document.removeEventListener('focusin', preventChatFocus)
        clearTimeout(timeoutId)
      }
    }
  }, [sidepanelState, focusIntent, setFocusIntent])

  // When returning from a View to Home via Mini, focus chat input
  const wasOnViewRef = useRef(canGoBack)
  useEffect(() => {
    const wasOnView = wasOnViewRef.current
    wasOnViewRef.current = canGoBack
    // Only fire when navigation actually lands back on Home
    if (wasOnView && !canGoBack && sidepanelState === 'open') {
      const timeoutId = setTimeout(() => {
        setFocusIntent(null)
        document.getElementById(ChatInputElementId)?.focus()
      }, FOCUS_AFTER_OPEN_DELAY)
      return () => {
        clearTimeout(timeoutId)
        setFocusIntent(null)
      }
    }
  }, [canGoBack, sidepanelState, setFocusIntent])

  const focusRemindersInput = useCallback(() => {
    // When the Reminders View is open, prefer its input over the sidepanel's
    const target =
      topViewId === 'reminders' ? document.querySelector<HTMLElement>('[data-view-top] [data-reminders-input]') : null
    ;(target ?? document.querySelector<HTMLElement>('[data-reminders-input]'))?.focus()
  }, [topViewId])

  // When focus intent is 'reminders' and sidepanel opens, focus after animation
  useEffect(() => {
    if (focusIntent !== 'reminders' || sidepanelState !== 'open') return
    const timeoutId = setTimeout(() => {
      setFocusIntent(null)
      focusRemindersInput()
    }, FOCUS_AFTER_OPEN_DELAY)
    return () => clearTimeout(timeoutId)
  }, [focusIntent, sidepanelState, setFocusIntent, focusRemindersInput])

  return { focusRemindersInput }
}
